// Client-side edge-triggered threshold crossing detection, ported 1:1
// from btclock-api's real, production-verified server-side alert-engine
// (src/btclock_api/alert_engine/evaluator.py's `AlertEvaluator`/
// `_is_on_triggering_side`) -- NOT legacy/dashboard.html's simpler
// "permanent triggered flag + manual Reset button" model, which has no
// equivalent in the new alerts data model (AlertResponse has no
// `triggered` field; the server engine re-arms via cooldown instead, see
// routes/alerts.ts's AlertResponse / the alert_engine docstring).
//
// Why this exists client-side at all, given the server already does this:
// the server-side alert-engine's delivery is a log-only stub (no Firebase/
// APNs creds yet -- a known, Owner-accepted gap for phone push). This is
// the in-browser half of "a fired alert should have a chime and a pop-up"
// -- computed independently client-side from the live SSE ratio stream so
// the popup/chime fires immediately while the tab is open, without waiting
// on a push mechanism that doesn't exist yet. Firing twice (once server-
// side into a log nobody sees, once here in the browser) is intentional,
// not a bug: this is the only half a user actually experiences today.
//
// Semantics (must match evaluator.py exactly -- see tests/alertEvaluator.
// test.mjs, ported test-for-test from btclock-api's test_evaluator.py):
// - "Edge-triggered": fires the instant ratio transitions from NOT
//   satisfying the condition to satisfying it; never re-fires while it
//   stays on the triggering side.
// - "Cooldown": after firing, the same alert won't fire again until
//   `cooldown_seconds` have elapsed, even if it oscillates back onto the
//   triggering side during that window.
// - Re-arming needs BOTH cooldown elapsed AND a fresh edge (dropping off
//   the triggering side and crossing back) -- waiting out the cooldown
//   while continuously on the triggering side does NOT refire alone.
// - ratio === null (undefined -- bid depth was zero) never satisfies any
//   condition and is otherwise ignored.

export interface AlertThreshold {
  id: number;
  direction: "above" | "below";
  value: number;
  cooldown_seconds: number;
}

export interface FiredAlert {
  threshold: AlertThreshold;
  ratio: number;
  tsUtc: string;
}

function isOnTriggeringSide(direction: "above" | "below", ratio: number, value: number): boolean {
  return direction === "above" ? ratio > value : ratio < value;
}

export class AlertEvaluator {
  private lastOnSide = new Map<number, boolean>();
  private lastFiredMonotonicMs = new Map<number, number>();

  /** `nowMonotonicMs` should be a monotonic clock (e.g. `performance.now()`),
   * not wall-clock time, matching evaluator.py's own `now_monotonic`
   * parameter -- immune to system clock adjustments mid-session. */
  evaluateTick(
    ratio: number | null,
    tsUtc: string,
    nowMonotonicMs: number,
    thresholds: readonly AlertThreshold[]
  ): FiredAlert[] {
    const fired: FiredAlert[] = [];
    if (ratio === null) return fired;

    for (const threshold of thresholds) {
      const onSide = isOnTriggeringSide(threshold.direction, ratio, threshold.value);
      const wasOnSide = this.lastOnSide.get(threshold.id) ?? false;
      const isNewEdge = onSide && !wasOnSide;

      if (isNewEdge) {
        const lastFired = this.lastFiredMonotonicMs.get(threshold.id);
        const cooldownMs = threshold.cooldown_seconds * 1000;
        const cooledDown = lastFired === undefined || nowMonotonicMs - lastFired >= cooldownMs;
        if (cooledDown) {
          this.lastFiredMonotonicMs.set(threshold.id, nowMonotonicMs);
          fired.push({ threshold, ratio, tsUtc });
        }
      }

      this.lastOnSide.set(threshold.id, onSide);
    }

    return fired;
  }
}
